import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ModalDirective, ModalModule } from 'ngx-bootstrap/modal';
import { ApiNewsService } from '../../../../../../../services/news/api/api-news.service';
import { AlertsService } from '../../../../../../../services/alerts.service';
import { LanguagesService } from '../../../../../../../services/languages/languages.service';
import { NewsUser } from '../../../../../../../models/news/news-user.model';
import { NewsPredefined } from '../../../../../../../models/news/news-predefined.model';
import { NewsPredefinedTranslation } from '../../../../../../../models/news/news-predefined-translation.model';

@Component({
  selector: 'app-news-user-edit-modal',
  templateUrl: './news-user-edit-modal.component.html',
  standalone: true,
  imports: [ModalModule, CommonModule, FormsModule]
})
export class NewsUserEditModalComponent {

  @ViewChild('modal') modal!: ModalDirective;

  isLoading: boolean = false;
  newsId: number = 0;
  private userLocale: string = '';
  selectedPredefinedId: number = 0;
  title: string = '';
  summary: string = '';
  content: string = '';
  isRate: boolean = false;
  predefinedList: NewsPredefined[] = [];
  private filledFromPredefined: boolean = false;

  private callbackSaved: (() => void) | undefined;

  constructor(
    private apiNewsService: ApiNewsService,
    private alertsService: AlertsService,
    private languagesService: LanguagesService
  ) {}

  show(newsUser: NewsUser, userLocale: string, callbackSaved?: () => void): void {
    this.callbackSaved = callbackSaved;
    this.newsId = newsUser.newsId;
    this.userLocale = userLocale;
    this.selectedPredefinedId = newsUser.newsPredefinedId ?? 0;
    this.title = newsUser.title;
    this.summary = newsUser.summary;
    this.content = newsUser.content;
    this.isRate = newsUser.isRate;
    this.filledFromPredefined = false;
    this.isLoading = false;
    this.apiNewsService.getNewsPredefined(1, 1000).then(response => {
      this.predefinedList = response.results;
      if (this.selectedPredefinedId !== 0 && !this.title && !this.summary && !this.content) {
        this.fillFromPredefined(this.selectedPredefinedId);
      }
    });
    this.modal.show();
  }

  onPredefinedChange(): void {
    if (this.selectedPredefinedId === 0) {
      this.filledFromPredefined = false;
      return;
    }
    this.fillFromPredefined(this.selectedPredefinedId);
  }

  private fillFromPredefined(predefinedId: number): void {
    const predefined = this.predefinedList.find(p => p.newsPredefinedId === predefinedId);
    if (predefined && predefined.translations.length > 0) {
      const tr = this.pickTranslationForLocale(predefined.translations);
      this.title = tr.title;
      this.summary = tr.summary;
      this.content = tr.content;
      this.filledFromPredefined = true;
    }
  }

  private pickTranslationForLocale(translations: NewsPredefinedTranslation[]): NewsPredefinedTranslation {
    const locale = (this.userLocale ?? '').toLowerCase();
    if (locale) {
      const localePrefix = locale.split(/[_-]/)[0];
      const match = this.languagesService.languages.find(l => {
        const iso = (l.languageIso ?? '').toLowerCase();
        return iso === locale || iso.split(/[_-]/)[0] === localePrefix;
      });
      if (match) {
        const tr = translations.find(t => t.languageId === match.languageId);
        if (tr) return tr;
      }
    }
    return translations[0];
  }

  onFieldChanged(): void {
    if (this.filledFromPredefined) {
      this.selectedPredefinedId = 0;
      this.filledFromPredefined = false;
    }
  }

  save(): void {
    this.isLoading = true;
    const newsPredefinedId = this.selectedPredefinedId || null;
    this.apiNewsService.editNewsUser(this.newsId, newsPredefinedId, this.title, this.summary, this.content, this.isRate).then(
      () => {
        this.isLoading = false;
        this.modal.hide();
        this.alertsService.displayAlertSuccess('Message updated !');
        if (this.callbackSaved) { this.callbackSaved(); }
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error updating message : ' + error);
      }
    );
  }
}
